import { AppBar, Box, Divider, IconButton, makeStyles, Snackbar, Toolbar, Typography } from '@material-ui/core';
import Table from './Table';
import Uploader from './Uploader';
import { getBranchList } from '../../actions/commonAction';
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import ResendOrderSearchBar from '../../components/ResendOrderManagement/ResendOrderSearchbar';
import { labelPrint, loadCrOptions, loadResendOrders, loadRsOptions, uploadResendOrder } from '../../actions/resendOrderActions';
import { handleEditOrderModal, showAlertSnackbar } from '../../reducers/uiSlice';
import { useTranslation } from 'react-i18next';
import FullscreenLoading from '../../components/Common/FullscreenLoading';
import UploadResendOrderModal from '../../components/Modals/UploadResendOrderModal';
import _ from 'lodash';
import ResendOrderTable from '../../components/ResendOrderManagement/ResendOrderTable';
import { formatTimestamp, getBranchById } from '../../utilities/common';
import EditOrderModal from '../../components/EditOrderModal';
import Close from '@material-ui/icons/Close';
import { useHistory } from 'react-router-dom';
import { Alert } from '@material-ui/lab';
import { useToken } from '../../utilities/hooks';

const orderStatus = {
    190: "ORDER_RECEIVED",
    198: "CUSTOM_RELEASE_DIRECT",
    199: "GATEWAY_TRANSIT",
    200: "PARCEL_SCANNED",
    202: "IN_TRANSIT",
    203: "DELIVERED",
    204: "TRANSSHIPMENT",
    206: "WRONG_ADDRESS_FROM_TRANSIT",
    207: "PARCEL_LOST",
    209: "OTHER_EXCEPTION",
    211: "RETURN_OFFICE_FROM_TRANSIT",
    212: "WRONG_ADDRESS_FROM_RECEIVE",
    213: "STORAGE_30_DAYS_FROM_OFFICE",
    215: "PARCEL_ABANDONED",
    216: "SELF_PICK_UP",
    217: "TRANSSHIPMENT_COMPLETE",
    218: "SCANNED_PARCEL_MISSING",
    219: "WRONG_ROUTE_PARCEL",
    220: "SECOND_DELIVERY",
    221: "RETURNED_PARCEL_SCANNED",
    222: "REJECTED_PARCEL_FROM_TRANSIT",
    223: "CHANGED_ORDER_RESENT",
    224: "RESENT_ORDER_VOIDED",
    225: "FORWARDED_3RDPARTY",
    230: "RETURN_TO_SENDER_WAREHOUSE",
    255: "MAIN_PORT_GATEWAY_TRANSIT"


}

const useStyles = makeStyles((theme)=> ({
    root: {
        backgroundColor: theme.palette.background.default,
        height: '100vh'
    },
    headerContainer: {
        margin: theme.spacing(1),
    }
}))

function ResendOrderManagement() {
    const {t} = useTranslation('resendOrders');
    const dispatch = useDispatch();
    const classes = useStyles();
    const history = useHistory();
    const tokenContents = useToken();

    const userUS = tokenContents.userUS;

    const [branches, setBranches] = useState([]);
    const [batchNos, setBatchNos] = useState([]);
    const [requestNos, setRequestNos] = useState([]);

    const [mergedOrderList, setMergedOrderList] = useState([]);

    const [loading, setLoading]= useState(false);
    const [uploadProgress, setUploadProgress] = useState(undefined);
    const [openUploadModal, setOpenUploadModal] = useState(false);

    const [rescheduleList, setRescheduleList] = useState([]);
    const [invalidOrdersCount, setInvalidOrdersCount] = useState(0);
    const [rescheduleOrdersCount, setRescheduleOrdersCount] = useState(0);

    useEffect(() => {
        dispatch(getBranchList())
        .then((branches) => {
            setBranches(branches);
        });

        fetchResendData()

        setRescheduleList([]);
        setInvalidOrdersCount(0);
        setRescheduleOrdersCount(0);

    }, []);
    
    useEffect(() => {
        // Enable label print only within US
        if(userUS) {
            setColumns((prev) => [
                ...prev,
                {
                    field: 'tno', 
                    headerName: t('print'), 
                    width: '5%', 
                    align: 'center', 
                    hasButton: true,
                    buttonAction: (value)=>{
                        dispatch(labelPrint(value))
                        .then((res) => {
                            window.open(res.Item[0].Url, '_blank');
                        })
                        .catch(() => {
                            dispatch(showAlertSnackbar({message: t('print_fail'), type: 'error'}))
                        })
                    },
                    type: 'print'
                }
            ])
        }
    }, [])

    function handleCopy() {
        let orders = '';
        rescheduleList.map((order) => {
            orders += ',' + order.original_tno; 
        })
        orders = orders.substring(1);
        navigator.clipboard.writeText(orders);
        dispatch(showAlertSnackbar({title: t('copy_reschedule_orders'), message: t('copy_success'), type: 'success'}))
    }

    const handleClose = () => {
        history.goBack();
    };
    
    const handleRescheduleClose = () => {
        handleCopy();
        setRescheduleList([]);
        setInvalidOrdersCount(0);
        setRescheduleOrdersCount(0);
    };

    const getBranchName = (id) => {
        let warehouse = getBranchById(id, branches)
        if(warehouse === undefined){
            return warehouse
        }
        return warehouse.short_name
    }

    const getStateName = (state) => {
        if(_.has(orderStatus, state) && state !== undefined){
            return orderStatus[state]
        }
        return state
    }

    const translateState = (state) => {
        if(state === undefined){
            return state
        }
        return t(`orderStatus:${getStateName(state)}`)
    }

    const getFormattedTimestamp = (timestamp) => {
        if(timestamp === undefined){
            return timestamp
        }
        return formatTimestamp(timestamp, "MM-DD HH:mm")
    }

    const [columns, setColumns] = useState([
        {field: 'id', headerName: '#', width: '3%'},
        {
            field: 'tno', 
            headerName: t('tracking_number'), 
            width: '10%', 
            align: 'center', 
            hasButton: true,
            buttonAction: (value)=>{
                dispatch(handleEditOrderModal({open: true, orderIdentifier: value, type: 'tno'}))
            },
            type: 'edit'
        },
        {field: 'address1', headerName: t('address'), width: '30%', align: 'center'},
        {field: 'city', headerName: t('city'), width: '10%', align: 'center'},
        {field: 'consignee', headerName: t('consignee'), width: '10%', align: 'center'},
        {field: 'state', headerName: t('tracking_state'), width: '10%', align: 'center', valueGetter: translateState},
        {field: 'province', headerName: t('province'), width: '5%', align: 'center'},
        {field: 'update_time', headerName: t('update_time'), width: '10%', align: 'center', valueGetter: getFormattedTimestamp},
        {field: 'warehouse', headerName: t('warehouse'), width: '5%', align: 'center', valueGetter: getBranchName},
        {field: 'storage_info', headerName: t('storage_info'), width: '7%', align: 'center'}
    ]);

    const handleBatchSelected = async (batch) => {
        setLoading(true)
        try{
            let resendRequestNo = await dispatch(loadCrOptions(batch))
            setRequestNos(resendRequestNo)
        }catch(error){
            dispatch(showAlertSnackbar({title: t('resend_order'), message: error.message}))
        }
        setLoading(false)
    }

    const handleFetchData = async (batches, request) => {
        if(batches.length <= 0 || request.length <= 0){
            dispatch(showAlertSnackbar({title: t('resend_order'), message: t('Request number is required')}))
            return
        }
        setLoading(true)
        try{
            let combinedOrders = await dispatch(loadResendOrders(request, batches))
            let mergedOrders = []
            _.forEach(combinedOrders, (order, index) => {
                let cloneOrder
                //prevent empty data
                if(order['old_order'] !== null && order['new_order'] === null){
                    cloneOrder = order['old_order']
                } else if(order['old_order'] === null && order['new_order'] !== null){
                    cloneOrder = order['new_order']
                } else {
                    cloneOrder = order['old_order']
                }
                let difference= {}
                //No need to run this if there is nothing to compare
                if(order['old_order'] !== null && order['new_order'] !== null){
                    difference = aggregateDifferences(order['old_order'], order['new_order'], {})
                }
                cloneOrder['id'] = index + 1
                _.merge(cloneOrder, difference)
                mergedOrders.push(cloneOrder)
            })
            setMergedOrderList(mergedOrders)
        }catch(error){
            dispatch(showAlertSnackbar({title: t('resend_order'), message: error.message}))
        }
        setLoading(false)
    }

    const toggleOpenUploadModal = () => {
        setOpenUploadModal(!openUploadModal)
    }

    const handleUploadResendOrder = async (data) => {
        // if(data.warehouse === undefined || data.req_date === undefined || data.file === undefined){
        if (data.req_date === undefined || data.file === undefined) {
            dispatch(showAlertSnackbar({title: t('resend_order'), message: "All inputs required"}))
            return
        }

        const uploadProgress = {
            onUploadProgress: (progressEvent) => {
                let percentCompleted = Math.round( (progressEvent.loaded * 100) / progressEvent.total );
                setUploadProgress(percentCompleted)
            }
        }
        setLoading(true)
        try{
            let uploadRes = await dispatch(uploadResendOrder(data, uploadProgress))
            setUploadProgress(undefined)

            //Since API WILL RETURN SUCCESS not matter what
            //Checking/error has to be all handled via client
            //IF backend fix their problem and return the correct fail codes, these can be removed
            let invalidOrdersCount = uploadRes['invalid_orders_count']
            let validOrderCount = uploadRes['valid_orders_count']

            let rescheduleList = uploadRes['rescheduled_delivery_orders'];
            let rescheduleOrdersCount = uploadRes['rescheduled_delivery_orders_count'];

            if(invalidOrdersCount > 0 && validOrderCount <= 0){
                //All Failed, stops the function and returns
                // dispatch(showAlertSnackbar({title: t('resend_order'), message: `All ${invalidOrdersCount} order(s) failed!`}))
                setInvalidOrdersCount(invalidOrdersCount);
                setRescheduleList(rescheduleList);
                setRescheduleOrdersCount(rescheduleOrdersCount);
                return
            }

            if(invalidOrdersCount > 0 && validOrderCount > 0){
                //Partial success
                dispatch(showAlertSnackbar({
                    title: t('resend_order'), 
                    message: `Partial Success: ${invalidOrdersCount} Failed. ${validOrderCount} Passed`, 
                    type: 'warning'
                }))
            } else {
                //All Success
                dispatch(showAlertSnackbar({
                    title: t('resend_order'), 
                    message: `${validOrderCount} orders has been successfully uploaded`, 
                    type: 'success'
                }))
            }

            //Close modal & fetch new data
            toggleOpenUploadModal()
            fetchResendData()
        }catch(error){
            dispatch(showAlertSnackbar({title: t('resend_order'), message: error.message}))
        }
        setLoading(false)
    }

    const fetchResendData = async () => {
        setLoading(true)
        try{
            let resendBatches = await dispatch(loadRsOptions())
            setBatchNos(resendBatches)
            // let resendRequestNo = await dispatch(loadCrOptions(resendBatches))
            // setRequestNos(resendRequestNo)
        }catch(error){
            dispatch(showAlertSnackbar({title: t('resend_order'), message: error.message}))
        }
        setLoading(false)
    }

    //Returns only the different fields and values
    const aggregateDifferences = (oldData, newData, result) => {
        Object.keys(oldData).forEach(function (k) {
            if (typeof oldData[k] !== 'object') {
                if (oldData[k] != newData[k]) result = {...result, [`new_${k}`]: newData[k]};
            } else {
                aggregateDifferences(oldData[k], newData[k], this);
            }
        }, result);
    
        return result;
    }

    const handleEditOrderClose = () => {
        dispatch(handleEditOrderModal({open: false}))
    }

    return (
        <Box className={classes.root}>
            <Snackbar open={(rescheduleList && rescheduleList.length > 0) || invalidOrdersCount} autoHideDuration={10000} onClose={handleRescheduleClose}>
                <Alert onClose={handleRescheduleClose} severity="error">
                    {
                        invalidOrdersCount &&
                        <div>
                            {t("invalid_orders_count")}{invalidOrdersCount}
                        </div>
                    }
                    {
                        rescheduleOrdersCount &&
                        <div>
                            {t("reschedule_orders_count")}{rescheduleOrdersCount}
                        </div>
                    }
                    
                    {
                        rescheduleList &&
                        rescheduleList.length > 0 &&
                        <div>
                            <div>{t('reschedule_order_list')}</div>
                            {
                                rescheduleList.map((reschedule) => (
                                    <div>
                                        {reschedule.original_tno}
                                    </div>
                                ))
                            }
                        </div>
                    }
                </Alert>
            </Snackbar>
            <AppBar position='static'>
                <Toolbar variant="dense">
                    <Typography 
                        variant='h6' 
                        color="inherit" 
                        style={{flexGrow: 1}}
                    >
                        {t('resend_order')}
                    </Typography>
                    <IconButton 
                        color="inherit"
                        onClick={handleClose}
                    >
                        <Close />
                    </IconButton>
                </Toolbar>
            </AppBar>
            <div className={classes.headerContainer}>
                <ResendOrderSearchBar
                    batches={batchNos}
                    requestNos={requestNos}
                    onBatchSelection={handleBatchSelected}
                    onFetchData={handleFetchData}
                    onOpenUploadModal={toggleOpenUploadModal}
                />
            </div>
            <Divider />
            <ResendOrderTable
                columns={columns}
                rows={mergedOrderList}
                filterModel={{
                    items: [
                        {columnField: 'warehouse', displayName: t('warehouse'), type: 'select', selection: branches},
                        {columnField: 'new_warehouse', displayName: t('new_warehouse'), type: 'select', selection: branches},
                        {columnField: 'state', displayName: t('status'), type: 'select', selection: [{id: 223, name: t('change_resend')}]},
                        {columnField: 'new_state', displayName: t('new_status'), type: 'select', selection: [{id: 223, name: t('change_resend')}]},
                    ]
                }}
            />
            {/* <CloseIcon onClick={handleClose} style={{float: 'right', color: 'white'}}/>
            <Uploader branches={branches}/>
            <Table branches={branches}/> */}
            <UploadResendOrderModal 
                open={openUploadModal}
                branches={branches} 
                onClose={toggleOpenUploadModal}
                onFinish={(data)=>{handleUploadResendOrder(data)}}
            />
            <EditOrderModal 
                byResendOrder={true}
                onClose={handleEditOrderClose}
                handleClose={handleEditOrderClose}
            />
            <FullscreenLoading open={loading} progress={uploadProgress}/>
        </Box>
    );
}

export default ResendOrderManagement;